import { useState, useEffect, useContext } from 'react';
import { useSessionQuery, useSessionMutation, mutationErrorMsg } from '../MsalAccessProvider';

// import { Modal } from 'react-responsive-modal';
// import 'react-responsive-modal/styles.css';
import Modal from 'react-modal';

import { UserContext } from '../UserProvider';
import { Panel, PanelActionButton, PanelModalEdit, PanelPrefs } from "../Panel";

import loading from '../../loading.gif';
import { discordInvite } from '../../authConfig';

Modal.setAppElement('#root');

const Profile = () => {
  const { user } = useContext(UserContext);
  const {data, error, isError, isLoading } = useSessionQuery('/profile');
  const [open, setOpen] = useState(false);

  const mutationDisplayName = useSessionMutation('/profile/updateDisplayName');
  const mutationNotification = useSessionMutation('/profile/updateNotification');
  const mutationUnregister = useSessionMutation('/profile/unregister');

  // editable state
  const [discordName, setDiscordName] = useState();
  const [displayName, setDisplayName] = useState({name: null, error: null});
  const [notifications, setNotifications] = useState();

  const customStyles = {
    content: {
      height: 'min-content',
      border: '1px solid #f05023',
      background: '#2a2929'
    }
  };

  // set editable data from initial pull
  useEffect(() => {
    if (data) {
      setDiscordName(data.discord_name);
      setDisplayName({
        name: data.display_name || user.first_name + ' ' + user.last_name,
        error: null
      });
      setNotifications(data.notifications);
    }
  }, [data, user]);

  // wait for initial pull
  if (isLoading) {
    return (
      <div className="flex flex-col my-8">
        <div className="flex justify-center pt-4 pb-2">
          <img alt="Loading..." src={loading} className='w-12' />
        </div>
      </div>
    );
  }
  
  if (isError) {
    return (
      <div className="flex flex-col my-8">
        <div className="text-center pt-4 pb-2">
          {error}
        </div>
        <div className="text-center pt-4 pb-2">
          Please contact us at csci@utrgv.edu from your UTRGV email.
        </div>
      </div>
    );
  }

  /////////////////////////
  // unregister action
  /////////////////////////

  const confirmUnregister = async () => {
    setOpen(true);
  }

  const unregister = async () => {
    mutationUnregister.mutateAsync({}, {
      onSuccess: (response, payload, context) => {
        setDiscordName(null);
        setOpen(false);
      }
    });
  };

  /////////////////////////
  // display name change action
  /////////////////////////

  // local validation on each change
  const displayNameChange = async (e) => {
    const name = e.target.value;
    // 32 chars, only letters and spaces
    if (name.trim().length > 32) {
      setDisplayName({name: name, error: "Display name can only be 32 characters long."});
    } else if (name.replace(/[a-z,A-Z]/g, '').trim().length !== 0) {
      setDisplayName({name: name, error: "Display name can only have letters and spaces - no numbers or special characters."});
    } else {
      setDisplayName({name: name, error: ""});
    }
  };

  // duplicate check on change button press
  const updateDisplayName = async (verificationCallback) => {
    // already under pre-stroke error, don't bother
    if (displayName.error) return false;

    const name = displayName.name.trim();
    if (!name) {
      // blank, don't bother the server
      setDisplayName({name: name, error: "Display name cannot be blank."});
      return;
    }
    
    mutationDisplayName.mutateAsync({displayName: name}, {
      onSuccess: (response, payload, context) => {
        if (!response.data.success) {
          setDisplayName({name: name, error: "Display name already in use, please choose another."});
        } else {
          setDisplayName({name: name, error: null});
          // call verificationCallback only if the value is verified
          if (verificationCallback) verificationCallback();
        }  
      }
    });
  };

  /////////////////////////
  // notification change action
  /////////////////////////

  const updateNotification = async (changeIndex, value) => {
    mutationNotification.mutateAsync({
      roleId: notifications[changeIndex].id,
      subscribed: value
    }, {
      onSuccess: (response, payload, context) => {
        if (response.data.success) {
          setNotifications(notifications => notifications.map((notification, i) => 
          i === changeIndex ? {...notification, subscribed: value} : notification));
        }
      }
    });
  };

  /////////////////////////
  // render
  /////////////////////////

  return (
    <>
      {/* <SubNavbar /> */}
        <div className="flex flex-col mt-6 mx-8 space-y-6 md:tracking-wide">

          {discordName ?
          <>
          <Panel title="Discord User"
            description={
            <div>
              If you need to switch discord accounts, unregister your current one here first,
              then get a new registration link from the @Control bot on your new account.
            </div>}
            loadingText={mutationUnregister && mutationUnregister.isLoading && ('Unregistering...')}
            errorText={mutationUnregister && mutationUnregister.isError && ('Failed to unregister')}
            >
            <PanelActionButton buttonLabel="Unregister" onClick={confirmUnregister}>{data.discord_name}</PanelActionButton>
          </Panel>
          </>
          :
          <Panel title="Discord User"
              description={
                <>
                  <div>
                    To register your discord user account for access to the CS discord server:
                  </div>
                  <ul className="">
                      <li className="ml-4 mt-2">Step 1: join the server here: <a className="text-utrgv-blue semibold" href={discordInvite}>{discordInvite}</a></li>
                      <li className="ml-4 mt-2">Step 2: send a message to the @Control bot in order to get a registration link
                      (further instructions are there in the discord server)</li>
                  </ul>
                </>}>
          </Panel>
          }

          {data.has_registration &&
            <>
              <Panel title="Discord Display Name"
                description={
                <div className="space-y-4">
                  <p>The CS discord is <span className="italic">not anonymous</span>! Your instructors
                  know who you are, and we expect you to behave online the same way you do in our
                  classrooms.</p>
                  <p>We strongly recommend using the name you go by in-person, as you will be
                  interacting and networking with your peers in and out of class. However, if you
                  would prefer to appear with a different (reasonable, non-offensive) name,
                  you can enter it here. Letters and spaces only.</p>
                </div>}
                loadingText={mutationDisplayName && mutationDisplayName.isLoading && ('Updating display name...')}
                errorText={mutationErrorMsg(mutationDisplayName, 'Failed to update display name')}
                >
                <PanelModalEdit onChange={displayNameChange}
                          value={displayName.name}
                          errorMsg={displayName.error}
                          buttonLabel="Change" onClick={updateDisplayName}/>
              </Panel>

              <Panel title="Discord Notifications"
                description={<div>Disable to opt-out of notifications for the CS discord
                announcement channels. Only faculty and staff can make announcements.
                </div>}
                loadingText={mutationNotification && mutationNotification.isLoading && ('Updating notification...')}
                errorText={mutationErrorMsg(mutationNotification, 'Failed to update notification')}
                >
                <PanelPrefs prefs={notifications && notifications.map(notification => {
                  return {label: notification.name, description: notification.topic,
                          checked: notification.subscribed};
                })}
                onChange={updateNotification}
                />
              </Panel>
            </>}

        </div>

      <Modal
        isOpen={open}
        onRequestClose={() => setOpen(false)}
        style={customStyles}
        contentLabel="Confirmation"
      >
        <div className="flex flex-col space-y-4 rounded border-utrgv-orange justify-center">
          <div className="p-3 rounded bg-cod-gray-mid text-center text-utrgv-lightgray">
            Are you sure you want to unregister <span className="text-white">{data && data.discord_name}</span>?
          </div>
          <div className="flex flex-row justify-center pb-4">
            <button
            className="rounded px-4 py-1 my-auto ml-3 w-28 border border-white hover:bg-utrgv-blue"
            onClick={unregister}>Unregister
            </button>
            <button
            className="rounded px-4 py-1 my-auto ml-3 w-28 border border-white hover:bg-utrgv-orange"
            onClick={() => setOpen(false)}>Cancel
            </button>
          </div>
        </div>
      </Modal>

    </>
  );
};

export default Profile;
